"use client";

import Icon from "@afa-shared/afa-components/dist/Icon";
import { ContentLink } from "@components/Link/ContentLink";
import { StandardLink } from "@components/Link/StandardLink";
import { Shortcut } from "@components/Shortcut";
import { hasVisibleChildrenForAudience } from "@utils/menuHelper";
import { getMobileMenu } from "@utils/menuHelper";
import clsx from "clsx";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import useMediaQueryWrapper from "hooks/useMediaQuery";
import { useMobileNavigationOpenState } from "hooks/zustand/useMobileNavigationOpenState";
import { ISectorToggler, useSectorToggler } from "hooks/zustand/useSectorToggle";
import { MenuItem } from "types/MenuItem";
import { MobileMenuShortcut } from "types/MobileMenuShortcut";

import { resolveSlugAndPath } from "..";
import { navigationTracker } from "../NavigationTracker";
import navigationStyles from "../navigation.module.css";
import mobileNavigationStyle from "./Mobile.module.css";

const getMobileShortcutLinkColor = (index: number) => {
  if (index <= 2) return "#7dcdff";
  if (index === 3) return "#37E164";
  if (index === 4 || index === 5) return "#003CD2";
  return "#C8EBFA";
};

const MobileNavigation = ({
  menuItems: mobileMenuItems,
  mobileMenuShortcuts,
  mobileMenuId,
  is404,
}: {
  menuItems: MenuItem[];
  mobileMenuShortcuts?: MobileMenuShortcut[];
  mobileMenuId?: string;
  is404: boolean;
}): React.ReactNode | any => {
  const pathname = usePathname();
  const isTablet = useMediaQueryWrapper("tablet");
  const { path, slug } = resolveSlugAndPath(pathname, isTablet);

  const isBrandPortal = pathname.startsWith("/varumarke") && !is404;
  const isCampaignPage =
    (pathname.startsWith("/intressant-vetande") ||
      pathname.startsWith("/ingen-missad-ersattning") ||
      pathname.startsWith("/tank-pa-oss")) &&
    !is404;
  const selectedSector = useSectorToggler((state: ISectorToggler) => state.selectedSector);
  const [hasHydrated, setHasHydrated] = useState(false);
  const effectiveSector = hasHydrated ? selectedSector : undefined;
  const effectiveAudienceId = effectiveSector?.audienceId;
  const mobileMenu: MenuItem[] = useMemo(
    () => getMobileMenu(mobileMenuItems, path, effectiveSector, isTablet),
    [mobileMenuItems, path, effectiveSector, isTablet],
  );
  const [menuItems, setMenuItems] = useState<Array<MenuItem>>(mobileMenu);
  const isForsakringPages = slug === "forsakring" || pathname.startsWith("/forsakring/");
  const navigationIsOpen = useMobileNavigationOpenState((state) => state.mobileNavigationIsOpen);
  const setMobileNavigationIsOpen = useMobileNavigationOpenState(
    (state) => state.setMobileNavigationIsOpen,
  );

  const pathName = usePathname() ?? "";

  const hasCustomMenu: boolean = isBrandPortal || isCampaignPage;

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  useEffect(() => {
    if (!hasHydrated) {
      return;
    }

    setMenuItems(mobileMenu);
    return () => {
      setMenuItems([]);
    };
  }, [mobileMenu, pathName, effectiveAudienceId, hasHydrated]);

  const renderSubMenu = (item: MenuItem, levelTwoParent: string) => {
    return (
      <ul
        className={clsx(mobileNavigationStyle.list, {
          [mobileNavigationStyle.isOpen]: item.isOpened,
        })}
        id={item.id}
        data-level={item?.level + 1}
      >
        {item.children.map((child: MenuItem) =>
          getMenuItemsRecursively(
            child,
            levelTwoParent,
            hasHydrated && child?.hideForAudiences?.includes(effectiveAudienceId),
          ),
        )}
      </ul>
    );
  };
  const TrackAndCloseMenu = (item, section, event) => {
    console.log("target closest", event);
    navigationIsOpen
      ? document.body.classList.remove("mobile-menu-open")
      : document.body.classList.add("mobile-menu-open");
    setMobileNavigationIsOpen(!navigationIsOpen);
    item?.level === 1
      ? navigationTracker(item?.name, item?.name, "headermeny")
      : navigationTracker(item?.name, section, "submeny");
  };
  const toggleItemRecursively = (item: MenuItem, array: Array<MenuItem>): Array<MenuItem> => {
    for (const menuItem of array) {
      if (menuItem?.id === item?.id) {
        menuItem.isOpened = !menuItem.isOpened;
      } else {
        toggleItemRecursively(item, menuItem?.children);
      }
    }
    return array;
  };

  const ToggleDropDown = (item: MenuItem) => {
    if (!item?.children?.length) return;
    const array = toggleItemRecursively(item, menuItems);
    setMenuItems([...array]);
  };
  const getMenuItemsRecursively = (item: MenuItem, levelTwoParent: string, hidden: boolean) => {
    if (!item || item?.hideInMenu) return null;
    const section = item?.level === 2 ? item?.name : levelTwoParent;
    const hasActiveHiddenChildren =
      !item?.isActive &&
      item?.hasActiveChildren &&
      !hasVisibleChildrenForAudience(item, effectiveAudienceId) &&
      item?.slug !== "varumarke" &&
      item?.slug !== "intressant-vetande" &&
      item?.slug !== "ingen-missad-ersattning";
    return (
      <li
        className={clsx(mobileNavigationStyle.listItem, {
          [mobileNavigationStyle.active]: item?.isActive || hasActiveHiddenChildren,
          [mobileNavigationStyle.open]: !hidden,
        })}
        key={item?.id}
        data-level={item?.level}
      >
        {item?.isContainer && hasVisibleChildrenForAudience(item, effectiveAudienceId) ? (
          <button
            className={clsx(mobileNavigationStyle.labelButton, {
              [mobileNavigationStyle.active]: item?.isActive || hasActiveHiddenChildren,
              [mobileNavigationStyle.inActivePath]: item?.hasActiveChildren,
            })}
            aria-expanded={item?.isOpened}
            aria-controls={item.id}
            aria-label={`Växla undermeny för ${item?.name}`}
            onClick={() => {
              ToggleDropDown(item);
            }}
            data-level={item?.level}
            data-testid={`page-header-mobile-container-${item?.slug}`}
          >
            {item?.name}
            {item?.children?.length ? (
              <Icon
                name={item?.isOpened ? "remove" : "add"}
                className={mobileNavigationStyle.toggleIcon}
              />
            ) : null}
          </button>
        ) : (
          <div
            className={clsx(mobileNavigationStyle.label, {
              [mobileNavigationStyle.active]: item?.isActive || hasActiveHiddenChildren,
            })}
            data-level={item?.level}
          >
            <ContentLink
              href={item?.redirectsTo ? item?.redirectsTo : item?.url}
              className={clsx(mobileNavigationStyle.labelLink, {
                [mobileNavigationStyle.active]: item?.isActive || hasActiveHiddenChildren,
                [mobileNavigationStyle.inActivePath]: item?.hasActiveChildren,
              })}
              onClick={(event) => TrackAndCloseMenu(item, section, event)}
              data-level={item?.level}
              data-testid={`page-header-mobile-link-${item?.slug}`}
              contentText={item?.name}
              noTracking={true}
            >
              {item?.name}
            </ContentLink>

            {item?.children?.length && hasVisibleChildrenForAudience(item, effectiveAudienceId) ? (
              <button
                className={mobileNavigationStyle.iconButton}
                aria-expanded={item?.isOpened}
                aria-controls={item.id}
                aria-label={`Växla undermeny för ${item?.name}`}
                data-testid={`page-header-mobile-button-${item?.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  ToggleDropDown(item);
                }}
              >
                <Icon
                  name={item?.isOpened ? "remove" : "add"}
                  className={mobileNavigationStyle.toggleIcon}
                />
              </button>
            ) : null}
          </div>
        )}
        {item?.children?.length ? renderSubMenu(item, section) : null}
      </li>
    );
  };

  return (
    <div
      className={clsx(navigationStyles.mobileMenuWrapper, {
        [navigationStyles.useTopPadding]: isForsakringPages,
        [navigationStyles.mobileMenuClosed]: !navigationIsOpen,
      })}
    >
      <div
        data-testid={"mobile-menu"}
        className={clsx(mobileNavigationStyle.mobileNavigation, {
          [mobileNavigationStyle.isOpen]: navigationIsOpen,
        })}
        id={mobileMenuId}
      >
        <ul
          className={clsx(mobileNavigationStyle.list, {
            [mobileNavigationStyle.isOpen]: navigationIsOpen,
          })}
          data-level={1}
        >
          {mobileMenuItems?.map((item: MenuItem) => {
            const hidden = hasHydrated && item?.hideForAudiences?.includes(effectiveAudienceId);
            return getMenuItemsRecursively(item, "", hidden);
          })}
        </ul>
        {hasCustomMenu ? (
          <StandardLink
            text={"Gå till Afa Försäkrings webbplats"}
            href={"/"}
            variant="button"
            buttonStyle="secondary"
            onClick={() =>
              navigationTracker(
                "Gå till Afa Försäkrings webbplats",
                "Gå till Afa Försäkrings webbplats",
                "submeny",
              )
            }
            noTracking={true}
          />
        ) : (
          <ul className={mobileNavigationStyle.shortcutUl}>
            {mobileMenuShortcuts?.map((item: MobileMenuShortcut, index: number) => {
              const itemUrl = item?.url;
              return (
                <li key={index}>
                  <Link
                    href={itemUrl}
                    prefetch={false}
                    className={mobileNavigationStyle.shortcutLink}
                    onClick={() => {
                      setMobileNavigationIsOpen(false);
                      document.body.classList.remove("mobile-menu-open");
                    }}
                  >
                    {/* todo: remove overrideIconBackground prop when we have icons with background. this is not a great solution */}
                    <Shortcut
                      icon={item?.icon}
                      label={item?.label}
                      text={item?.text}
                      className={mobileNavigationStyle.shortcut}
                      removeContentBorder
                      overrideIconBackground={getMobileShortcutLinkColor(index)}
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default MobileNavigation;
